import { useCallback, useSyncExternalStore } from "react";
import { API_BASE } from "../lib/apiBase";

// One EventSource per tab to `${API_BASE}/api/stream`, shared by every
// subscriber (the server caps connections per IP, so N feeds must not mean
// N sockets). Events are notifications — "feed X has new data" — and callers
// refetch the REST route; nothing here carries or caches data.
//
// Reconnects: on error the socket is closed and rebuilt with exponential
// backoff + jitter. The rebuilt URL carries ?lastEventId= so the server can
// replay missed events (a browser only sends the Last-Event-ID header on its
// own automatic reconnects, which we pre-empt to control the backoff).
//
// Honesty: the server sends a heartbeat every 25 s. If nothing at all (event
// or heartbeat) arrives for 60 s the socket is presumed dead even if the
// browser still thinks it is open (sleeping laptop, proxy that swallowed the
// stream) — status drops to "offline", the indicator says POLLING, and the
// socket is rebuilt. Data keeps flowing through useFeed's polling meanwhile.

export type LiveStatus = "connecting" | "live" | "reconnecting" | "offline";
export type LiveEventName = "feed" | "connector" | "alert" | "resync";

export interface LiveMessage {
  event: LiveEventName;
  id: string;
  data: Record<string, unknown>;
}

type Listener = (msg: LiveMessage) => void;

interface EventSourceLike {
  onopen: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  addEventListener: (type: string, fn: (ev: MessageEvent) => void) => void;
  close: () => void;
}
type EventSourceCtor = new (url: string) => EventSourceLike;

const EVENTS: LiveEventName[] = ["feed", "connector", "alert", "resync"];
const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 60_000;
/** After this many consecutive failures the indicator says POLLING, not RECONNECTING. */
const OFFLINE_AFTER_ATTEMPTS = 5;
/** Keep the socket briefly after the last subscriber leaves (StrictMode remounts). */
const IDLE_CLOSE_MS = 2_000;
/** No event or heartbeat for this long → the stream is dead, whatever readyState says. */
export const STALE_AFTER_MS = 60_000;
const STALE_CHECK_MS = 10_000;

interface Config {
  EventSourceImpl: EventSourceCtor | undefined;
  baseUrl: string;
  random: () => number;
}

function defaultConfig(): Config {
  const impl = typeof globalThis.EventSource === "function"
    ? (globalThis.EventSource as unknown as EventSourceCtor)
    : undefined;
  return { EventSourceImpl: impl, baseUrl: API_BASE, random: Math.random };
}

let config: Config = defaultConfig();
const listeners = new Set<Listener>();
const statusListeners = new Set<() => void>();
let status: LiveStatus = "connecting";
let es: EventSourceLike | null = null;
let attempts = 0;
let lastEventId = "";
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let onlineHooked = false;
let lastSeenAt = 0;
let staleTimer: ReturnType<typeof setInterval> | null = null;

function stopStaleCheck(): void {
  if (staleTimer) { clearInterval(staleTimer); staleTimer = null; }
}

function startStaleCheck(): void {
  if (staleTimer) return;
  staleTimer = setInterval(() => {
    if (!es || status !== "live") return;
    if (Date.now() - lastSeenAt <= STALE_AFTER_MS) return;
    es.close();
    es = null;
    attempts++;
    setStatus("offline");
    scheduleReconnect(backoffDelay(attempts, config.random));
  }, STALE_CHECK_MS);
}

function setStatus(next: LiveStatus): void {
  if (status === next) return;
  status = next;
  for (const fn of statusListeners) fn();
}

/** Exponential backoff with equal jitter: [d/2, d], d = min(max, base·2^(n-1)). */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const d = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1));
  return Math.round(d / 2 + random() * (d / 2));
}

function emit(msg: LiveMessage): void {
  for (const fn of listeners) {
    try { fn(msg); } catch { /* one bad subscriber never breaks the rest */ }
  }
}

function connect(): void {
  const Impl = config.EventSourceImpl;
  if (!Impl) { setStatus("offline"); return; }
  const qs = lastEventId ? `?lastEventId=${encodeURIComponent(lastEventId)}` : "";
  const source = new Impl(`${config.baseUrl}/api/stream${qs}`);
  es = source;
  source.onopen = () => {
    attempts = 0;
    lastSeenAt = Date.now();
    setStatus("live");
    startStaleCheck();
  };
  source.onerror = () => {
    if (es !== source) return;
    source.close();
    es = null;
    attempts++;
    setStatus(attempts >= OFFLINE_AFTER_ATTEMPTS ? "offline" : "reconnecting");
    scheduleReconnect(backoffDelay(attempts, config.random));
  };
  // Heartbeats only prove liveness; they are not forwarded to subscribers.
  source.addEventListener("heartbeat", () => { lastSeenAt = Date.now(); });
  for (const name of EVENTS) {
    source.addEventListener(name, (ev: MessageEvent) => {
      lastSeenAt = Date.now();
      if (ev.lastEventId) lastEventId = ev.lastEventId;
      let data: Record<string, unknown> = {};
      try {
        const parsed: unknown = JSON.parse(String(ev.data));
        if (parsed && typeof parsed === "object") data = parsed as Record<string, unknown>;
      } catch { /* malformed → empty payload; still a notification */ }
      emit({ event: name, id: ev.lastEventId ?? "", data });
    });
  }
}

function scheduleReconnect(ms: number): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (hasSubscribers()) connect();
  }, ms);
}

function hasSubscribers(): boolean {
  return listeners.size > 0 || statusListeners.size > 0;
}

function ensureStarted(): void {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
  if (es || retryTimer) return;
  if (!onlineHooked && typeof window !== "undefined") {
    onlineHooked = true;
    // Network came back: skip the remaining backoff.
    window.addEventListener("online", () => {
      if (!es && hasSubscribers()) {
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
        connect();
      }
    });
  }
  connect();
}

function maybeStop(): void {
  if (hasSubscribers() || idleTimer) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (hasSubscribers()) return;
    es?.close();
    es = null;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    stopStaleCheck();
    attempts = 0;
    setStatus("connecting");
  }, IDLE_CLOSE_MS);
}

/** Subscribe to every stream message. Opens the shared socket on first use. */
export function subscribeLive(fn: Listener): () => void {
  listeners.add(fn);
  ensureStarted();
  return () => {
    listeners.delete(fn);
    maybeStop();
  };
}

/**
 * Call `onChange` when a `feed` event names one of `keys`, or on `resync`
 * (the server could not replay a gap — everything may be stale).
 */
export function subscribeFeedKeys(keys: readonly string[], onChange: (msg: LiveMessage) => void): () => void {
  const wanted = new Set(keys);
  return subscribeLive((msg) => {
    if (msg.event === "resync") onChange(msg);
    else if (msg.event === "feed" && typeof msg.data.key === "string" && wanted.has(msg.data.key)) onChange(msg);
  });
}

export function getLiveStatus(): LiveStatus {
  return status;
}

function subscribeStatus(fn: () => void): () => void {
  statusListeners.add(fn);
  ensureStarted();
  return () => {
    statusListeners.delete(fn);
    maybeStop();
  };
}

/** React binding: live connection status plus the subscribe API. */
export function useLiveStream(): { status: LiveStatus; subscribe: (fn: Listener) => () => void } {
  const current = useSyncExternalStore(subscribeStatus, getLiveStatus, () => "offline" as LiveStatus);
  const subscribe = useCallback((fn: Listener) => subscribeLive(fn), []);
  return { status: current, subscribe };
}

/** Test hook — swap EventSource/base/random and drop all state. */
export function resetLiveStreamForTests(next: Partial<Config> = {}): void {
  es?.close();
  es = null;
  stopStaleCheck();
  lastSeenAt = 0;
  if (retryTimer) clearTimeout(retryTimer);
  if (idleTimer) clearTimeout(idleTimer);
  retryTimer = null;
  idleTimer = null;
  listeners.clear();
  statusListeners.clear();
  attempts = 0;
  lastEventId = "";
  status = "connecting";
  config = { ...defaultConfig(), ...next };
}
